import type { TFunction } from 'i18next';
import type { Dayjs } from 'dayjs';
import moment from 'moment';
import type { Locale as AntdLocale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import arEG from 'antd/locale/ar_EG';
import { localizeDigitsForLocale, usesArabicIndicDigits } from './arabicDigits';
import { getMomentLocale } from './momentLocale';

export const CALENDAR_WEEKDAY_KEYS = [
  'common.calendar.weekday-sun',
  'common.calendar.weekday-mon',
  'common.calendar.weekday-tue',
  'common.calendar.weekday-wed',
  'common.calendar.weekday-thu',
  'common.calendar.weekday-fri',
  'common.calendar.weekday-sat',
] as const;

export const CALENDAR_MONTH_KEYS = [
  'common.calendar.month-january',
  'common.calendar.month-february',
  'common.calendar.month-march',
  'common.calendar.month-april',
  'common.calendar.month-may',
  'common.calendar.month-june',
  'common.calendar.month-july',
  'common.calendar.month-august',
  'common.calendar.month-september',
  'common.calendar.month-october',
  'common.calendar.month-november',
  'common.calendar.month-december',
] as const;

export const CALENDAR_MONTH_SHORT_KEYS = [
  'common.calendar.month-short-january',
  'common.calendar.month-short-february',
  'common.calendar.month-short-march',
  'common.calendar.month-short-april',
  'common.calendar.month-short-may',
  'common.calendar.month-short-june',
  'common.calendar.month-short-july',
  'common.calendar.month-short-august',
  'common.calendar.month-short-september',
  'common.calendar.month-short-october',
  'common.calendar.month-short-november',
  'common.calendar.month-short-december',
] as const;

export const getCalendarWeekdayLabels = (t: TFunction): string[] =>
  CALENDAR_WEEKDAY_KEYS.map((key) => t(key));

export const getCalendarMonthLabel = (monthIndex: number, t: TFunction): string =>
  t(CALENDAR_MONTH_KEYS[monthIndex] ?? CALENDAR_MONTH_KEYS[0]);

export const getCalendarMonthShortLabel = (
  monthIndex: number,
  t: TFunction,
  locale?: string
): string =>
  // Arabic does not abbreviate Gregorian month names in the product UI.
  usesArabicIndicDigits(locale)
    ? getCalendarMonthLabel(monthIndex, t)
    : t(CALENDAR_MONTH_SHORT_KEYS[monthIndex] ?? CALENDAR_MONTH_SHORT_KEYS[0]);

export const formatCalendarMonthYear = (
  monthIndex: number,
  year: number,
  t: TFunction,
  locale?: string
): string =>
  localizeDigitsForLocale(
    `${getCalendarMonthLabel(monthIndex, t)} ${year}`,
    locale
  );

export const formatCalendarDayMonth = (
  day: number,
  monthIndex: number,
  t: TFunction,
  locale?: string
): string =>
  localizeDigitsForLocale(
    `${day} ${getCalendarMonthShortLabel(monthIndex, t, locale)}`,
    locale
  );

export type ChartGroupBy = 'day' | 'week' | 'month';

/** Stable numeric key for sorting/grouping chart data points. */
export const getChartGroupSortKey = (
  date: Dayjs,
  groupBy: ChartGroupBy
): number => {
  if (groupBy === 'month') {
    return date.startOf('month').valueOf();
  }
  if (groupBy === 'week') {
    return date.startOf('isoWeek').valueOf();
  }
  return date.startOf('day').valueOf();
};

/** Locale-aware x-axis label for dashboard line charts. */
export const formatChartAxisLabel = (
  date: Dayjs,
  groupBy: ChartGroupBy,
  locale: string,
  t: TFunction
): string => {
  const m = moment(date.toDate()).locale(getMomentLocale(locale));
  const monthToken = usesArabicIndicDigits(locale) ? 'MMMM' : 'MMM';
  let formatted: string;
  if (groupBy === 'month') {
    formatted = m.format(`${monthToken} YYYY`);
  } else if (groupBy === 'week') {
    formatted = `${t('submissions.week')} ${m.isoWeek()} ${m.format(`${monthToken} YYYY`)}`;
  } else {
    formatted = m.format(`DD ${monthToken} YYYY`);
  }
  return localizeDigitsForLocale(formatted, locale);
};

type DisplayDateInput = string | Date | Dayjs | null | undefined;

const DISPLAY_DATE_PARSE_FORMATS = [
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'DD/MM/YY',
  moment.ISO_8601,
];

const parseDisplayDate = (date: DisplayDateInput): moment.Moment => {
  if (date == null || date === '') return moment.invalid();

  if (typeof date === 'object' && date !== null && 'toDate' in date) {
    return moment((date as Dayjs).toDate());
  }

  if (typeof date === 'string') {
    const trimmed = date.trim();
    if (!trimmed) return moment.invalid();
    const strict = moment(trimmed, DISPLAY_DATE_PARSE_FORMATS, true);
    if (strict.isValid()) return strict;
    const loose = moment(trimmed);
    return loose.isValid() ? loose : moment.invalid();
  }

  const parsed = moment(date);
  return parsed.isValid() ? parsed : moment.invalid();
};

/** Locale-aware date string with Arabic-Indic digits when the UI is Arabic. */
export const formatDisplayDate = (
  date: DisplayDateInput,
  format: string,
  locale?: string | null
): string => {
  if (date == null || date === '') return '';
  const parsed = parseDisplayDate(date);
  if (!parsed.isValid()) {
    return localizeDigitsForLocale(String(date).trim(), locale);
  }
  // Arabic: never abbreviate months (MMM → MMMM). MMMM is left unchanged.
  const resolvedFormat = usesArabicIndicDigits(locale)
    ? format.replace(/M{3,4}/g, 'MMMM')
    : format;
  return localizeDigitsForLocale(
    parsed.locale(getMomentLocale(locale ?? 'en')).format(resolvedFormat),
    locale
  );
};

/** DD/MM/YYYY for transaction tables; LTR-isolated in Arabic RTL layouts. */
export const formatTransactionTableDate = (
  date: DisplayDateInput,
  locale?: string | null,
  empty = '-'
): string => {
  if (date == null || date === '') return empty;
  const formatted = formatDisplayDate(date, 'DD/MM/YYYY', locale);
  if (!formatted) return empty;
  return usesArabicIndicDigits(locale)
    ? `\u2066${formatted}\u2069`
    : formatted;
};

export const formatDisplayDateRange = (
  from: DisplayDateInput,
  to: DisplayDateInput,
  format: string,
  locale?: string | null,
  separator = ' - '
): string => {
  const fromText = formatDisplayDate(from, format, locale);
  const toText = formatDisplayDate(to, format, locale);
  if (!fromText) return toText;
  if (!toText) return fromText;
  const range = `${fromText}${separator}${toText}`;
  // Keep chronological order when this string sits inside an RTL container.
  return usesArabicIndicDigits(locale) ? `\u2066${range}\u2069` : range;
};

/** Ant Design DatePicker/RangePicker `format` callback with locale digits. */
export const getLocalizedDatePickerFormat = (
  format: string,
  locale?: string | null
) => {
  return (value: Dayjs) => formatDisplayDate(value, format, locale);
};

export const formatCalendarDayMonthYear = (
  day: number,
  monthIndex: number,
  year: number,
  t: TFunction,
  locale?: string
): string =>
  localizeDigitsForLocale(
    `${String(day).padStart(2, '0')} ${getCalendarMonthLabel(monthIndex, t)} ${year}`,
    locale
  );

export const getPickerLocale = (t: TFunction, language?: string) => {
  const isArabic = usesArabicIndicDigits(language);
  const shortWeekDays = getCalendarWeekdayLabels(t);
  const months = CALENDAR_MONTH_KEYS.map((key) => t(key));
  // Arabic: short month slots use the full Gregorian names.
  const shortMonths = isArabic
    ? months
    : CALENDAR_MONTH_SHORT_KEYS.map((key) => t(key));

  return {
    ...enUS.DatePicker,
    lang: {
      ...enUS.DatePicker.lang,
      // Drive dayjs month/year header from the active UI language (not en_US).
      locale: isArabic ? 'ar' : 'en_US',
      monthBeforeYear: !isArabic,
      shortWeekDays,
      shortMonths,
      months,
      today: t('common.calendar.today'),
      now: t('common.calendar.today'),
      backToToday: t('common.calendar.today'),
      placeholder: t('common.calendar.select-date'),
      rangePlaceholder: [
        t('common.calendar.range-start'),
        t('common.calendar.range-end'),
      ],
    },
  };
};

export const getPaginationLocale = (t: TFunction) => ({
  ...enUS.Pagination,
  items_per_page: t('main.pagination.items-per-page'),
});

export const buildAntdLocale = (t: TFunction, language: string): AntdLocale => {
  const isArabic = language.startsWith('ar');
  const base = isArabic ? arEG : enUS;
  const datePickerLocale = getPickerLocale(t, language);

  return {
    ...base,
    DatePicker: {
      ...base.DatePicker,
      ...datePickerLocale,
      lang: {
        ...base.DatePicker?.lang,
        ...datePickerLocale.lang,
      },
    },
    Calendar: {
      ...base.Calendar,
      ...datePickerLocale,
      lang: {
        ...base.Calendar?.lang,
        ...datePickerLocale.lang,
      },
    },
    Pagination: getPaginationLocale(t),
  };
};
